import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import { useAppStore } from '@core/store'
import ControlosDoRealce from '@viewer/realce/ControlosDoRealce'
import { useAuth } from '@core/auth/useAuth'
import { useNomesDosModelos } from '@viewer/lib/useNomeDoModelo'
import { whenRecoveryResolved } from '@core/session/recovery'
import type { ClashResult } from '@viewer/clash/lib'
import ClashResultsList from './ClashResultsList'
import ClashSetsEditor from './ClashSetsEditor'
import { acrescentarNovosAoModulo, exportarTopicoDoModelador, exportClashesToBcf } from './clashBcfExport'
import type { VersaoBcf } from '@modules/bcf/lib/bcfBuilder'
import {
  applyClashHighlight,
  clearClashHighlight,
  isClashHighlightActive,
  refreshClashHighlight,
  registerClashSurface,
  releaseClashSurface,
} from './clashHighlight'
import {
  goToClash,
  hasPreviousView,
  isolateClash,
  restorePreviousView,
  showAllAgain,
} from './clashNavigate'
import {
  allCategoriesOf,
  applyPreset,
  emptySide,
  MEP_CATEGORIES,
  STRUCTURE_CATEGORIES,
  type ClashGroupBy,
} from './clashSets'
import {
  clashKey,
  clashLabel,
  missingTriaged,
  PRIORITIES,
  PRIORITY_LABELS,
  sugestoesDeEspecialidade,
  triageCounts,
  triageOf,
  TRIAGE_LABELS,
  type ClashPriority,
  type ClashTriagePatch,
  type ClashTriageState,
} from './clashTriage'
import { conflitosAExportar, itensParaExportar, temGrupos } from './clashBcf'
import { latestParsed } from '@modules/bcf/store/bcfClientSlice'
import { formatarInteiro } from '@modules/bcf/lib/bcfAgrupar'
import { alteracoesParaModulo, conflitosNovosParaModulo } from './clashModuloBcf'
import { dataCurtaPt, resultadosResolvidosAusentes } from './clashReconciliacao'
import { deltaDesdeUltima } from './clashExportacaoRegisto'
import { ExportacaoCancelada, resolveClashGuids } from './clashBcfExport'
import { casarProposta, lerProposta, resumoProposta } from './clashProposta'
import { escolherReferencial, modelosGeoDaCena } from './clashReferencial'
import ClashTriageEditor from './ClashTriageEditor'
import ClashClassificacaoPainel from './ClashClassificacaoPainel'
import { useClashRun } from './useClashRun'
import VisibilityCheck from './VisibilityCheck'
import ClashPartilhaBarra, { ClashPartilhaEdicao } from './ClashPartilhaBarra'
import { editarConflito, motorDaPartilha, usePartilhaDaTriagem } from './clashPartilhaControlo'
import ClashExclusaoPainel from './ClashExclusaoPainel'
import ClashGruposLista from './ClashGruposLista'
import { classificarEAgrupar } from './clashClassificarEAgrupar'
import {
  contarPrioridades,
  estadoNoBcf,
  textoDoResumoDaCorrida,
  type SeparadorDosConflitos,
} from './clashSeparadores'
import { avaliarExclusoes, especialidadeDosModelos, type RegraDeExclusao } from './clashExclusao'
import { PARES_DE_FOLGA, textoDasExcluidas, textoDoFunilAntesDeCorrer } from './clashFiltroEntrada'
import {
  contagensDoTopo,
  filtrarConflitos,
  linhasDosGrupos,
  opcoesDosFiltros,
  SEM_FILTROS,
  temFiltros,
  textoDoTopo,
  type FiltrosRapidos,
} from './clashPorGrupos'

// ---------------------------------------------------------------------------
// O banco de trabalho dos conflitos: conjuntos, configuracao, corrida,
// triagem e exportacao.
//
// E O MESMO em dois sitios: no painel flutuante do ribbon (atalho rapido,
// e o unico caminho em MODO LOCAL) e na pagina do modulo Conflitos. Nao
// ha duas copias da logica nem dois estados: tudo o que interessa vive no
// `clashSlice`, por isso correr no painel e ver o resultado na pagina (e
// ao contrario) sai de graca. Foi a licao do IDS quando passou a modulo:
// validar num deixava o outro em branco.
//
// A ordem do ecra e a ordem do trabalho: definir os dois conjuntos,
// afinar a configuracao, correr, triar, exportar. Os conjuntos estao no
// topo e nao escondidos numa engrenagem porque sao a decisao que faz a
// diferenca entre 929 conflitos e 30.
//
// LARGURA. Acoplado por baixo do 3D (o modo natural para rever
// conflitos: modelo em cima, lista em baixo) o espaco e largo e baixo,
// e ai a configuracao vai para uma coluna a esquerda e a lista fica com
// a altura toda. Num painel estreito e tudo uma coluna. A decisao e por
// CONTENTOR (`@container`), nao por janela: o dock nao tem nada a ver
// com o tamanho do ecra.
// ---------------------------------------------------------------------------

function mmValue(metres: number): string {
  const mm = metres * 1000
  return Number.isInteger(mm) ? String(mm) : String(Number(mm.toFixed(3)))
}

function parseMm(text: string): number {
  const n = Number(text.replace(',', '.'))
  return Number.isFinite(n) && n > 0 ? n / 1000 : 0
}

export interface ClashWorkbenchProps {
  /** Falso quando a superficie esta escondida: nao se paga inventario
   *  nem regras por um painel fechado. */
  active?: boolean
}

export default function ClashWorkbench({ active = true }: ClashWorkbenchProps) {
  const models = useAppStore((s) => s.models)
  const { nomeDe } = useNomesDosModelos()
  const selectionCount = useAppStore((s) => s.selectedTargets.length)
  const { mode, modules, user, workspace } = useAuth()
  const bcfModuleEnabled = mode === 'cloud' && modules.includes('bcf')

  const sideA = useAppStore((s) => s.clashSideA)
  const sideB = useAppStore((s) => s.clashSideB)
  const setSide = useAppStore((s) => s.setClashSide)
  const preset = useAppStore((s) => s.clashPreset)
  const setPreset = useAppStore((s) => s.setClashPreset)
  const tolerance = useAppStore((s) => s.clashTolerance)
  const clearance = useAppStore((s) => s.clashClearance)
  const ignoreContacts = useAppStore((s) => s.clashIgnoreContacts)
  const selfClash = useAppStore((s) => s.clashSelfClash)
  const setTolerance = useAppStore((s) => s.setClashTolerance)
  const setClearance = useAppStore((s) => s.setClashClearance)
  const setIgnoreContacts = useAppStore((s) => s.setClashIgnoreContacts)
  const setSelfClash = useAppStore((s) => s.setClashSelfClash)
  const penetracaoMinima = useAppStore((s) => s.clashPenetracaoMinima)
  const setPenetracaoMinima = useAppStore((s) => s.setClashPenetracaoMinima)
  const folgaPares = useAppStore((s) => s.clashFolgaPares)
  const setFolgaPares = useAppStore((s) => s.setClashFolgaPares)
  const excluidasEntrada = useAppStore((s) => s.clashExcluidasEntrada)
  const classificacao = useAppStore((s) => s.clashClassificacao)

  const running = useAppStore((s) => s.clashRunning)
  const progress = useAppStore((s) => s.clashProgress)
  const results = useAppStore((s) => s.clashResults)
  const stats = useAppStore((s) => s.clashStats)
  const error = useAppStore((s) => s.clashError)
  const ranAt = useAppStore((s) => s.clashRanAt)
  const stale = useAppStore((s) => s.clashStale)
  const dismissStale = useAppStore((s) => s.dismissClashStale)
  const syncModels = useAppStore((s) => s.syncClashModels)
  const storedKey = useAppStore((s) => s.clashModelKey)

  const groupBy = useAppStore((s) => s.clashGroupBy)
  const setGroupBy = useAppStore((s) => s.setClashGroupBy)
  const focusKey = useAppStore((s) => s.clashFocusKey)
  const setFocusKey = useAppStore((s) => s.setClashFocusKey)
  const checked = useAppStore((s) => s.clashChecked)
  const toggleChecked = useAppStore((s) => s.toggleClashChecked)
  const setChecked = useAppStore((s) => s.setClashChecked)
  const applyTriageMany = useAppStore((s) => s.applyClashTriageMany)
  const isolated = useAppStore((s) => s.clashIsolated)
  const setIsolated = useAppStore((s) => s.setClashIsolated)
  // Cores, fantasma e caixa: cada um à parte (viewer/realce). O realce
  // está activo com qualquer um deles ligado.
  const realce = useAppStore((s) => s.realce)
  const highlightOn = realce.cores || realce.fantasma || realce.caixa

  const triage = useAppStore((s) => s.clashTriage)
  const hydrateTriage = useAppStore((s) => s.hydrateClashTriage)
  const hydrateSetup = useAppStore((s) => s.hydrateClashSetup)
  const applyTriage = useAppStore((s) => s.applyClashTriage)
  const forgetTriage = useAppStore((s) => s.forgetClashTriage)
  const pushToast = useAppStore((s) => s.pushToast)
  // Reconciliacao ao repetir a corrida: resumo, envio para o modulo BCF.
  const resumoCorrida = useAppStore((s) => s.clashResumoCorrida)
  const retida = useAppStore((s) => s.clashReconciliacaoRetida)
  const confirmarRetida = useAppStore((s) => s.confirmarReconciliacaoRetida)
  const descartarRetida = useAppStore((s) => s.descartarReconciliacaoRetida)
  const setResumoCorrida = useAppStore((s) => s.setClashResumoCorrida)
  const envioModulo = useAppStore((s) => s.clashModulo)
  const setEnvioModulo = useAppStore((s) => s.setClashModulo)
  const garantirProjectoBcf = useAppStore((s) => s.garantirProjectoBcf)
  const bcfLoaded = useAppStore((s) => s.bcfLoaded)
  const bcfUpdateTopic = useAppStore((s) => s.bcfUpdateTopic)
  const bcfAddComment = useAppStore((s) => s.bcfAddComment)
  const ultimaExportacao = useAppStore((s) => s.clashUltimaExportacao)
  const setUltimaExportacao = useAppStore((s) => s.setClashUltimaExportacao)

  const [showConfig, setShowConfig] = useState(false)
  const [exporting, setExporting] = useState(false)
  // Exportacao: imagens (por omissao sim), topicos com grupo, versao.
  // Com grupos, por omissao um topico por conflito com o grupo indicado
  // (etiqueta, RelatedTopics, linha na descricao); "um por grupo" junta
  // o grupo num so topico, como antes.
  const [incluirImagens, setIncluirImagens] = useState(true)
  const [topicosDosGrupos, setTopicosDosGrupos] = useState<'conflito' | 'grupo'>('conflito')
  // "Só conflitos com triagem": null = automático (ligado quando há
  // proposta importada, isto é, conflitos com grupo).
  const [soComTriagemEscolha, setSoComTriagem] = useState<boolean | null>(null)
  // Modelo em cujas coordenadas sai o BCF; '' = regra automática.
  const [referencialModelo, setReferencialModelo] = useState('')
  const [versaoBcf, setVersaoBcf] = useState<VersaoBcf>('2.1')
  // Levar tambem os triados que deixaram de ocorrer (resolvidos), para
  // as outras plataformas fecharem os topicos (Guids estaveis).
  const [incluirResolvidos, setIncluirResolvidos] = useState(false)
  // Exportacao para ficheiro: so o que mudou desde a ultima (novos e
  // alterados), com os mesmos Guids.
  const [soAlteracoesEscolha, setSoAlteracoes] = useState(false)
  // A acrescentar os conflitos novos ao BCF do modulo.
  const [acrescentando, setAcrescentando] = useState(false)
  const [progressoImagens, setProgressoImagens] = useState<{
    feitas: number
    total: number
  } | null>(null)
  const exportSignal = useRef<{ aborted: boolean } | null>(null)
  // O funil: lista por grupos ou por conflito (null = automatico: por
  // grupos quando ha grupos), filtros rapidos, painel das regras de
  // exclusao e a exportacao com ou sem os ignorados por regra.
  const [modoEscolha, setModo] = useState<'grupos' | 'conflitos' | null>(null)
  const [filtros, setFiltros] = useState<FiltrosRapidos>(SEM_FILTROS)
  const [exclusaoAberta, setExclusaoAberta] = useState(false)
  const [incluirIgnorados, setIncluirIgnorados] = useState(false)
  // Edicao em lote dos marcados.
  const [lotePrioridade, setLotePrioridade] = useState<ClashPriority | ''>('')
  const [loteResponsavel, setLoteResponsavel] = useState('')
  const [lotePrazo, setLotePrazo] = useState('')
  // Importacao da proposta de triagem.
  const propostaRef = useRef<HTMLInputElement>(null)
  const [propostaMsg, setPropostaMsg] = useState<string | null>(null)
  // Os três passos (clashSeparadores.ts). Sem escolha: Detetar antes da
  // primeira corrida, Rever depois.
  const [separadorEscolha, setSeparadorEscolha] = useState<{ id: SeparadorDosConflitos; ranAt: number } | null>(null)
  const [arrumarAoDetetar, setArrumarAoDetetar] = useState(true)
  const [arrumacao, setArrumacao] = useState<string | null>(null)
  const [arrumando, setArrumando] = useState(false)

  const clash = useClashRun({ active, autor: user?.email ?? null })
  const { inventories, countA, countB } = clash

  // Triagem partilhada no workspace (so com sessao e o modulo Conflitos).
  usePartilhaDaTriagem(active)
  // O conflito em foco e o que se esta a editar: os colegas veem-no
  // (bloqueio leve, so informativo).
  useEffect(() => {
    if (active) editarConflito(focusKey)
  }, [active, focusKey])

  // A triagem e a configuracao sao trabalho: so se leem depois de o
  // arranque decidir se recupera ou descarta a sessao anterior (beta.75).
  useEffect(() => {
    if (!active) return
    void whenRecoveryResolved().then(() => {
      hydrateTriage()
      hydrateSetup()
    })
  }, [active, hydrateTriage, hydrateSetup])

  // Os IFCs mudaram: os resultados descrevem elementos que ja nao estao
  // carregados e vao abaixo, com aviso. Mesmo padrao do IDS/Tabela.
  const modelKey = useMemo(
    () => models.map((m) => `${m.id} ${m.fileName}`).join('\n'),
    [models],
  )
  useEffect(() => {
    if (modelKey === storedKey) return
    syncModels(modelKey)
  }, [modelKey, storedKey, syncModels])

  // Quem mostra conflitos regista-se; o realce so se apaga quando sai o
  // ultimo (o painel e a pagina podem estar montados ao mesmo tempo).
  const surfaceToken = useRef({})
  useEffect(() => {
    const token = surfaceToken.current
    registerClashSurface(token)
    return () => {
      void releaseClashSurface(token)
    }
  }, [])

  // O funil (docs/CLASH.md, seccao 11): as regras de exclusao tiram da
  // lista de trabalho o que nao e trabalho de reuniao; os filtros rapidos
  // so mudam o que se ve.
  const especialidadeDe = useMemo(
    () => especialidadeDosModelos(models, classificacao.especialidades),
    [models, classificacao.especialidades],
  )
  const avaliacao = useMemo(
    () => avaliarExclusoes(results, triage, classificacao.exclusoes, especialidadeDe),
    [results, triage, classificacao.exclusoes, especialidadeDe],
  )
  const ignorados = avaliacao.ignorados
  const trabalho = useMemo(
    () => (ignorados.size > 0 ? results.filter((r) => !ignorados.has(clashKey(r))) : results),
    [results, ignorados],
  )
  const visiveis = useMemo(() => filtrarConflitos(trabalho, triage, filtros), [trabalho, triage, filtros])
  const opcoesFiltros = useMemo(() => opcoesDosFiltros(trabalho, triage), [trabalho, triage])
  const counts = useMemo(() => triageCounts(trabalho, triage), [trabalho, triage])
  const grupos = useMemo(() => temGrupos(results, triage), [results, triage])
  const topo = useMemo(() => {
    if (!grupos) return null
    const { linhas, semGrupo } = linhasDosGrupos(trabalho, triage, classificacao.grupos)
    return contagensDoTopo(linhas, semGrupo.length, ignorados.size)
  }, [grupos, trabalho, triage, classificacao.grupos, ignorados])
  const modo = modoEscolha ?? (grupos ? 'grupos' : 'conflitos')
  // A exportacao leva a lista de trabalho; os ignorados so a pedido.
  const baseExport = incluirIgnorados ? results : trabalho
  // Recalculado a cada render: o placement e o CRS dos modelos podem
  // mudar no painel Posição sem a lista de modelos mudar.
  const escolhaReferencial = escolherReferencial(modelosGeoDaCena(), referencialModelo || null)
  const modeloDoReferencial = models.find((m) => m.id === escolhaReferencial.modeloId)
  // O nome curto do modelo (o mesmo do resto da plataforma) no lugar do
  // ficheiro completo.
  const rotuloReferencial = modeloDoReferencial
    ? escolhaReferencial.rotulo.replace(
        modeloDoReferencial.fileName,
        nomeDe(modeloDoReferencial.id)?.curto ?? modeloDoReferencial.fileName,
      )
    : escolhaReferencial.rotulo
  const missing = useMemo(() => missingTriaged(results, triage), [results, triage])

  const focused = results.find((r) => clashKey(r) === focusKey) ?? null

  // O que a reconciliacao muda nos topicos enviados para o modulo BCF.
  // So se aplica depois de "Aplicar ao BCF".
  const bcfDoEnvio = envioModulo
    ? (bcfLoaded.find((b) => b.localId === envioModulo.bcfLocalId) ?? null)
    : null
  const propostaModulo = useMemo(() => {
    if (!envioModulo || !bcfDoEnvio) return null
    const topicos = latestParsed(bcfDoEnvio).topics.map((t) => ({
      guid: t.guid,
      title: t.title,
      status: t.status,
    }))
    const p = alteracoesParaModulo(envioModulo, triage, topicos)
    return p.alteracoes.length > 0 ? p : null
  }, [envioModulo, bcfDoEnvio, triage])

  const onAplicarAoModulo = () => {
    if (!envioModulo || !propostaModulo) return
    const autor = user?.email ?? null
    for (const a of propostaModulo.alteracoes) {
      if (a.estadoDe !== a.estadoPara) {
        bcfUpdateTopic(envioModulo.bcfLocalId, a.guid, { status: a.estadoPara }, autor)
      }
      bcfAddComment(envioModulo.bcfLocalId, a.guid, a.comentario, autor)
    }
    setEnvioModulo({ ...envioModulo, aplicadoAte: propostaModulo.ate })
    const n = propostaModulo.alteracoes.length
    pushToast(`${n} tópico${n === 1 ? '' : 's'} do BCF actualizado${n === 1 ? '' : 's'}.`, {
      type: 'success',
    })
  }

  // Desligar o realce repoe o viewer no acto; religar volta a pintar o
  // conflito em foco. O estado anterior (fantasma) e guardado e reposto
  // pelo `clashHighlight`.
  // Sem conflito em foco (nova corrida, troca de IFC) o realce tambem
  // sai: senao o par antigo ficava pintado e o modelo em fantasma sem
  // nada na lista que o explicasse.
  useEffect(() => {
    if (!highlightOn || !focused) {
      if (!highlightOn || isClashHighlightActive()) void clearClashHighlight()
      return
    }
    void applyClashHighlight(focused)
  }, [highlightOn, focused, realce])

  const applyPresetStructureMep = () => {
    const estrutura = applyPreset(STRUCTURE_CATEGORIES, inventories)
    const mepCats = applyPreset(MEP_CATEGORIES, inventories)
    setSide('a', { source: 'categories', models: [], categories: estrutura })
    setSide('b', { source: 'categories', models: [], categories: mepCats })
    // Estrutura e MEP costumam vir no MESMO ficheiro. Sem isto o motor
    // nunca testa pares dentro do mesmo modelo e a analise sai vazia.
    setSelfClash(true)
    setPreset('estrutura-mep')
    if (mepCats.length === 0 || estrutura.length === 0) {
      pushToast(
        mepCats.length === 0
          ? 'Este modelo não tem categorias MEP (condutas, tubagem, calhas). O conjunto B ficou vazio.'
          : 'Este modelo não tem categorias de estrutura. O conjunto A ficou vazio.',
        { type: 'info' },
      )
    }
  }

  const applyPresetModelPair = () => {
    const [first, second] = models
    setSide('a', {
      source: 'categories',
      models: first ? [first.id] : [],
      categories: allCategoriesOf(first ? [first.id] : [], inventories),
    })
    setSide('b', {
      source: 'categories',
      models: second ? [second.id] : [],
      categories: allCategoriesOf(second ? [second.id] : [], inventories),
    })
    setSelfClash(false)
    setPreset('modelos')
  }

  const applyPresetAll = () => {
    const todas = allCategoriesOf([], inventories)
    setSide('a', { ...emptySide(), categories: todas })
    setSide('b', { ...emptySide(), categories: todas })
    setSelfClash(true)
    setPreset('tudo')
  }

  const onOpenClash = (r: ClashResult) => {
    setFocusKey(clashKey(r))
    void (async () => {
      await goToClash(r)
      const prefs = useAppStore.getState().realce
      if (prefs.cores || prefs.fantasma || prefs.caixa) {
        await applyClashHighlight(r)
      }
    })()
  }

  const onTriage = (r: ClashResult, state: ClashTriageState) => {
    applyTriage(clashKey(r), { state, label: clashLabel(r), origem: 'manual' })
  }

  const onPatch = (r: ClashResult, patch: ClashTriagePatch) => {
    applyTriage(clashKey(r), { ...patch, label: clashLabel(r), origem: 'manual' })
  }

  const onSetChecked = (keys: string[], on: boolean) => {
    const next = new Set(checked)
    for (const k of keys) {
      if (on) next.add(k)
      else next.delete(k)
    }
    setChecked([...next])
  }

  const modelNames = models.map((m) => m.fileName)

  /** Aplica a prioridade, o responsável e o prazo escolhidos aos
   *  conflitos marcados. Campo deixado em branco não mexe. */
  const onApplyBatch = () => {
    const patch: ClashTriagePatch = {}
    if (lotePrioridade) patch.priority = lotePrioridade
    if (loteResponsavel.trim()) patch.assignedTo = loteResponsavel.trim()
    if (lotePrazo) patch.dueDate = lotePrazo
    if (Object.keys(patch).length === 0) return
    const alvo = results.filter((r) => checked.has(clashKey(r)))
    applyTriageMany(
      alvo.map(clashKey),
      (k) => ({ ...patch, label: clashLabel(alvo.find((r) => clashKey(r) === k)!), origem: 'manual' }),
    )
    pushToast(
      `Triagem aplicada a ${alvo.length} conflito${alvo.length === 1 ? '' : 's'}.`,
      { type: 'success' },
    )
  }

  const onImportProposal = async (file: File) => {
    try {
      const bruto: unknown = JSON.parse(await file.text())
      const lida = lerProposta(bruto)
      if (!lida.ok) {
        setPropostaMsg(lida.motivo)
        return
      }
      const guids = await resolveClashGuids(results)
      const casado = casarProposta(
        lida.proposta,
        results.map((r) => ({ key: clashKey(r), guids: guids[clashKey(r)] ?? [null, null] })),
      )
      const porKey = new Map(results.map((r) => [clashKey(r), r]))
      applyTriageMany(Object.keys(casado.patches), (k) => ({
        ...casado.patches[k],
        origem: 'proposta',
        ...(porKey.get(k) ? { label: clashLabel(porKey.get(k)!) } : {}),
      }))
      setPropostaMsg(resumoProposta(casado, lida.proposta.avisos.length))
      // Com proposta, a exportação passa a levar só o que tem triagem.
      setSoComTriagem(true)
    } catch {
      setPropostaMsg('Não foi possível ler esse ficheiro como JSON.')
    }
  }

  const onIsolate = async () => {
    if (!focused) return
    await isolateClash(focused)
    setIsolated(true)
    await refreshClashHighlight(focused)
  }

  const onShowAll = async () => {
    await showAllAgain()
    setIsolated(false)
    await refreshClashHighlight(focused)
  }

  const soComTriagem = soComTriagemEscolha ?? grupos
  const umPorGrupo = topicosDosGrupos === 'grupo' && grupos
  // So o que mudou: so para ficheiro e com uma exportacao anterior.
  const soAlteracoes = soAlteracoesEscolha && !bcfModuleEnabled && ultimaExportacao !== null
  const delta = useMemo(
    () =>
      soAlteracoes && ultimaExportacao
        ? deltaDesdeUltima({ results: baseExport, triage, ultima: ultimaExportacao, checked, soComTriagem, umPorGrupo })
        : null,
    [soAlteracoes, ultimaExportacao, baseExport, triage, checked, soComTriagem, umPorGrupo],
  )
  const toExport = (): ClashResult[] => {
    if (delta) return delta.aExportar
    const base = conflitosAExportar(baseExport, triage, { checked, soComTriagem })
    if (!incluirResolvidos || checked.size > 0) return base
    return [...base, ...resultadosResolvidosAusentes(triage, new Set(results.map(clashKey)))]
  }
  // Quantos topicos sai em cada opcao, para a escolha "por conflito" /
  // "por grupo" dizer o que da antes de exportar.
  const topicosPorOpcao = useMemo(() => {
    if (!grupos) return null
    const conflitos = toExport()
    return {
      conflito: conflitos.length,
      grupo: itensParaExportar(conflitos, triage, true).length,
    }
    // toExport le exactamente estas dependencias.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grupos, delta, baseExport, results, triage, checked, soComTriagem, incluirResolvidos])

  // Conflitos novos sem topico no BCF enviado para o modulo, pelo
  // criterio de exportacao em uso (marcados, por resolver, so com
  // triagem).
  const novosParaModulo = useMemo(() => {
    if (!bcfModuleEnabled || !envioModulo || !bcfDoEnvio) return []
    return conflitosNovosParaModulo(conflitosAExportar(baseExport, triage, { checked, soComTriagem }), envioModulo)
  }, [bcfModuleEnabled, envioModulo, bcfDoEnvio, baseExport, triage, checked, soComTriagem])

  // Os separadores (clashSeparadores.ts).
  const separador: SeparadorDosConflitos =
    separadorEscolha && separadorEscolha.ranAt === ranAt
      ? separadorEscolha.id
      : ranAt > 0
        ? 'rever'
        : 'detetar'
  const setSeparador = (id: SeparadorDosConflitos) => setSeparadorEscolha({ id, ranAt })
  const prioridades = useMemo(() => contarPrioridades(trabalho, triage), [trabalho, triage])
  const linhasNaLista = useMemo(
    () => (grupos ? linhasDosGrupos(visiveis, triage, classificacao.grupos).linhas.length : 0),
    [grupos, visiveis, triage, classificacao.grupos],
  )
  const estadoDoGrupoNoBcf = useMemo(
    () => estadoNoBcf(envioModulo, bcfDoEnvio ? latestParsed(bcfDoEnvio).topics : []),
    [envioModulo, bcfDoEnvio],
  )

  // Detetar e, se pedido, classificar e agrupar logo a seguir. A escolha
  // do separador vale para a corrida em que foi feita: uma corrida nova
  // abre Rever.
  const arrumarSinal = useRef<{ aborted: boolean } | null>(null)
  const detetar = async () => {
    const antes = useAppStore.getState().clashRanAt
    await clash.run()
    const st = useAppStore.getState()
    if (st.clashRanAt === antes || st.clashResults.length === 0 || !arrumarAoDetetar) return
    if (arrumarSinal.current) arrumarSinal.current.aborted = true
    const sinal = { aborted: false }
    arrumarSinal.current = sinal
    setArrumando(true)
    setArrumacao('A classificar e agrupar os conflitos…')
    try {
      const r = await classificarEAgrupar(st.clashResults, {
        signal: sinal,
        onProgress: (t) => {
          if (!sinal.aborted) setArrumacao(`A classificar e agrupar: ${t.toLowerCase()}`)
        },
      })
      if (sinal.aborted) return
      setArrumacao(
        `Classificados e agrupados: ${r.grupos} grupo${r.grupos === 1 ? '' : 's'}; `
          + `${r.mantidas} parecer${r.mantidas === 1 ? ' manual' : 'es manuais'} ou de proposta mantido${r.mantidas === 1 ? '' : 's'}; `
          + `${r.foraDoAmbito} fora do âmbito.`,
      )
    } catch (err) {
      if (!sinal.aborted) setArrumacao(err instanceof Error ? err.message : 'Não foi possível classificar e agrupar.')
    } finally {
      if (arrumarSinal.current === sinal) {
        arrumarSinal.current = null
        setArrumando(false)
      }
    }
  }

  const onAcrescentarAoModulo = async () => {
    if (!envioModulo || !bcfDoEnvio || novosParaModulo.length === 0) return
    const n = novosParaModulo.length
    const ok = window.confirm(
      `Acrescentar ${n} tópico${n === 1 ? '' : 's'} ao BCF "${bcfDoEnvio.name}"? `
        + 'Cada conflito novo ganha um tópico como na exportação; os do mesmo grupo ficam ligados '
        + 'aos tópicos do grupo que já lá estão (tópicos relacionados).',
    )
    if (!ok) return
    setAcrescentando(true)
    const signal = { aborted: false }
    exportSignal.current = signal
    try {
      const out = await acrescentarNovosAoModulo(novosParaModulo, envioModulo, {
        triage,
        grupos: useAppStore.getState().clashClassificacao.grupos,
        author: user?.email ?? null,
        projecto: garantirProjectoBcf(),
        imagens: incluirImagens,
        referencialModelo: referencialModelo || null,
        signal,
        onProgress: (p) => setProgressoImagens(p),
      })
      setEnvioModulo(out.envio)
      pushToast(
        `${out.topicos} tópico${out.topicos === 1 ? '' : 's'} acrescentado${out.topicos === 1 ? '' : 's'} ao BCF "${bcfDoEnvio.name}"`
          + (out.ligadosDeVolta > 0
            ? `; ${out.ligadosDeVolta} tópico${out.ligadosDeVolta === 1 ? '' : 's'} do mesmo grupo ligado${out.ligadosDeVolta === 1 ? '' : 's'} aos novos.`
            : '.'),
        { type: 'success' },
      )
    } catch (err) {
      if (err instanceof ExportacaoCancelada) {
        pushToast('Cancelado. A vista foi reposta e o BCF não foi alterado.', { type: 'info' })
      } else {
        pushToast(err instanceof Error ? err.message : 'Falha a acrescentar ao BCF.', { type: 'error' })
      }
    } finally {
      setAcrescentando(false)
      setProgressoImagens(null)
      exportSignal.current = null
    }
  }

  const onExport = async () => {
    const chosen = toExport()
    if (chosen.length === 0) {
      pushToast(
        delta
          ? 'Nada para exportar: nada mudou desde a última exportação.'
          : soComTriagem && checked.size === 0
          ? 'Nada para exportar: nenhum conflito por resolver tem triagem.'
          : 'Nada para exportar: todos os conflitos estão resolvidos.',
        { type: 'info' },
      )
      return
    }
    setExporting(true)
    const signal = { aborted: false }
    exportSignal.current = signal
    try {
      const out = await exportClashesToBcf(chosen, {
        bcfModuleEnabled,
        triage,
        grupos: useAppStore.getState().clashClassificacao.grupos,
        author: user?.email ?? null,
        // Guids estaveis: o mesmo conflito (ou grupo) sai sempre com o
        // mesmo Guid de topico; voltar a exportar actualiza os topicos.
        projecto: garantirProjectoBcf(),
        imagens: incluirImagens,
        umPorGrupo,
        referencialModelo: referencialModelo || null,
        versao: versaoBcf,
        ultimaExportacao,
        soAlteracoes: delta !== null,
        signal,
        onProgress: (p) => setProgressoImagens(p),
      })
      if (out.envio) setEnvioModulo(out.envio)
      if (out.registo) setUltimaExportacao(out.registo)
      const imagens =
        out.imagens > 0
          ? ` ${out.imagens} imagen${out.imagens === 1 ? 'm' : 's'}, ${out.msPorImagem} ms por imagem.`
          : ''
      pushToast(
        (out.destination === 'modulo'
          ? `${out.topics} tópico${out.topics === 1 ? '' : 's'} no BCF "${out.name}". Abre o módulo BCF para os rever.`
          : `${out.topics} tópico${out.topics === 1 ? '' : 's'} exportado${out.topics === 1 ? '' : 's'} para ${out.name}.`)
          + imagens,
        { type: 'success' },
      )
    } catch (err) {
      if (err instanceof ExportacaoCancelada) {
        pushToast('Exportação cancelada. A vista foi reposta.', { type: 'info' })
      } else {
        pushToast(
          err instanceof Error ? err.message : 'Falha a exportar para BCF.',
          { type: 'error' },
        )
      }
    } finally {
      setExporting(false)
      setProgressoImagens(null)
      exportSignal.current = null
    }
  }

  /** "Criar tópico para o modelador": um tópico com a regra e os
   *  elementos, no módulo BCF ou num .bcfzip (como a exportação). */
  const onTopicoModelador = async (regra: RegraDeExclusao, rs: ClashResult[]) => {
    try {
      const out = await exportarTopicoDoModelador(rs, regra, {
        bcfModuleEnabled,
        triage,
        especialidadeDe,
        author: user?.email ?? null,
        projecto: garantirProjectoBcf(),
        versao: versaoBcf,
        referencialModelo: referencialModelo || null,
      })
      pushToast(
        out.destination === 'modulo'
          ? `Tópico para o modelador criado no BCF "${out.name}" (${rs.length} conflitos num só tópico).`
          : `Tópico para o modelador exportado para ${out.name} (${rs.length} conflitos num só tópico).`,
        { type: 'success' },
      )
    } catch (err) {
      pushToast(err instanceof Error ? err.message : 'Falha a criar o tópico para o modelador.', { type: 'error' })
    }
  }

  /** Acção em lote de um grupo: o mesmo patch em todos os conflitos dele
   *  na lista (os ignorados por regra não estão lá e não mudam). */
  const onLoteDoGrupo = (keys: string[], patch: ClashTriagePatch) => {
    const porKey = new Map(results.map((r) => [clashKey(r), r]))
    applyTriageMany(keys, (k) => ({
      ...patch,
      origem: 'manual',
      ...(porKey.get(k) ? { label: clashLabel(porKey.get(k)!) } : {}),
    }))
    pushToast(`Aplicado a ${keys.length} conflito${keys.length === 1 ? '' : 's'} do grupo.`, { type: 'success' })
  }

  const pct =
    progress && progress.total > 0
      ? Math.min(100, Math.round((progress.done / progress.total) * 100))
      : 0
  const phaseLabel =
    progress?.phase === 'collect'
      ? 'A recolher caixas'
      : progress?.phase === 'broad'
        ? 'A cruzar candidatos'
        : progress?.phase === 'narrow'
          ? 'A testar geometria'
          : 'A concluir'

  const funilAntes = textoDoFunilAntesDeCorrer({ clearance, folgaPares, penetracaoMinima, ignoreContacts })
  const textoExcluidas = excluidasEntrada ? textoDasExcluidas(excluidasEntrada) : ''

  if (models.length === 0) {
    return (
      <div className="px-3 py-3 text-xs text-[var(--color-text-muted)]">
        Carrega um modelo IFC primeiro para detetar conflitos.
      </div>
    )
  }

  const tabs: { id: SeparadorDosConflitos; rotulo: string; pendentes?: number }[] = [
    { id: 'detetar', rotulo: '1 · Detetar' },
    {
      id: 'rever',
      rotulo: ranAt > 0
        ? `2 · Rever (${modo === 'grupos' && topo ? `${linhasNaLista} grupos` : `${counts.total} conflitos`})`
        : '2 · Rever',
    },
    {
      id: 'entregar',
      rotulo: '3 · Entregar BCF',
      pendentes: (propostaModulo?.alteracoes.length ?? 0) + (envioModulo && bcfDoEnvio ? novosParaModulo.length : 0),
    },
  ]

  return (
    <div
      data-testid="clash-workbench"
      className="flex min-h-0 flex-1 flex-col"
    >
      <VisibilityCheck />
      {stale && (
        <div className="flex shrink-0 items-center gap-2 border-b border-[var(--color-border)] bg-[var(--color-accent-bg)] px-3 py-2 text-[11px]">
          <AlertTriangle
            size={14}
            className="shrink-0 text-[var(--color-accent)]"
          />
          <span className="flex-1 leading-snug">
            Os modelos mudaram, por isso os resultados foram limpos. Corre
            outra vez para os ter do modelo carregado agora. Os estados de
            triagem e as regras ficam.
          </span>
          <button
            type="button"
            onClick={dismissStale}
            title="Fechar aviso"
            className="shrink-0 text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
          >
            ×
          </button>
        </div>
      )}
      <ClashPartilhaBarra />

      <div
        className="flex shrink-0 border-b border-[var(--color-border)] text-[11px]"
        role="tablist"
        aria-label="Passos dos conflitos"
        data-testid="clash-separadores"
      >
        {tabs.map((t) => {
          const activo = separador === t.id
          const semCorrida = t.id !== 'detetar' && ranAt === 0
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={activo}
              disabled={semCorrida}
              onClick={() => setSeparador(t.id)}
              title={semCorrida ? 'Primeiro detete os conflitos.' : undefined}
              className={`flex flex-1 items-center justify-center gap-1 border-b-2 px-2 py-1.5 disabled:opacity-40 ${
                activo
                  ? 'border-[var(--color-accent)] font-medium text-[var(--color-text)]'
                  : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
              }`}
            >
              {t.rotulo}
              {!!t.pendentes && t.pendentes > 0 && (
                <span
                  className="rounded-full bg-[var(--color-accent)] px-1.5 text-[10px] font-medium text-[var(--color-bg)]"
                  title="Alterações ou conflitos novos por levar ao BCF"
                >
                  {t.pendentes}
                </span>
              )}
            </button>
          )
        })}
      </div>

            {progressoImagens && (
              <div
                className="flex shrink-0 items-center gap-2 border-b border-[var(--color-border)] px-3 py-1.5 text-[11px]"
                role="status"
              >
                <span className="flex-1 text-[var(--color-text-muted)]">
                  A gerar imagens {progressoImagens.feitas}/{progressoImagens.total}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    if (exportSignal.current) exportSignal.current.aborted = true
                  }}
                  className="rounded border border-[var(--color-border)] px-1.5 py-0.5"
                >
                  Cancelar
                </button>
              </div>
            )}

            {retida && (
              <div
                className="flex shrink-0 flex-col gap-1 border-b border-[var(--color-border)] bg-[var(--color-warn-bg)] px-3 py-1.5 text-[11px]"
                role="alert"
                data-testid="clash-reconciliacao-retida"
              >
                <span className="flex items-start gap-1 leading-snug">
                  <AlertTriangle size={12} className="mt-0.5 shrink-0 text-[var(--color-warn-text)]" />
                  <span>
                    A última corrida deixava a triagem com {retida.depois} pareceres, em vez de{' '}
                    {retida.antes}. Nada foi gravado. Confirme se quer substituir a triagem
                    guardada; caso contrário, fica como estava e esta corrida não entra no
                    histórico.
                  </span>
                </span>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={descartarRetida}
                    className="rounded border border-[var(--color-accent)] px-1.5 py-0.5"
                  >
                    Manter a triagem guardada
                  </button>
                  <button
                    type="button"
                    onClick={confirmarRetida}
                    className="rounded border border-[var(--color-border)] px-1.5 py-0.5"
                  >
                    Gravar mesmo assim
                  </button>
                </div>
              </div>
            )}

      {arrumando && arrumacao && (
        <div
          className="flex shrink-0 items-center gap-2 border-b border-[var(--color-border)] px-3 py-1.5 text-[11px] text-[var(--color-text-muted)]"
          role="status"
          data-testid="clash-arrumacao"
        >
          <span className="flex-1 leading-snug">{arrumacao}</span>
          {!arrumando && (
            <button
              type="button"
              onClick={() => setArrumacao(null)}
              className="shrink-0 rounded border border-[var(--color-border)] px-1.5 py-0.5"
              title="Fechar aviso"
            >
              Fechar
            </button>
          )}
        </div>
      )}

      {separador === 'detetar' && (
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto" role="tabpanel" aria-label="Detetar">
          <div className="flex shrink-0 flex-col gap-1.5 border-b border-[var(--color-border)] px-3 py-2 text-[11px]">
            <div className="flex flex-wrap items-center gap-1">
              <span className="text-[var(--color-text-dim)]">Conjuntos:</span>
              <button
                type="button"
                onClick={applyPresetStructureMep}
                className={`rounded border px-1.5 py-0.5 ${preset === 'estrutura-mep' ? 'border-[var(--color-accent)] text-[var(--color-text)]' : 'border-[var(--color-border)]'}`}
                title="Vigas, pilares, lajes e paredes contra condutas, tubagem, calhas e equipamento."
              >
                Estrutura × MEP
              </button>
              <button
                type="button"
                onClick={applyPresetModelPair}
                disabled={models.length < 2}
                className={`rounded border px-1.5 py-0.5 disabled:opacity-40 ${preset === 'modelos' ? 'border-[var(--color-accent)] text-[var(--color-text)]' : 'border-[var(--color-border)]'}`}
                title={
                  models.length < 2
                    ? 'Precisa de dois modelos carregados'
                    : 'Primeiro modelo contra o segundo (federação)'
                }
              >
                Modelo A × Modelo B
              </button>
              <button
                type="button"
                onClick={applyPresetAll}
                className={`rounded border px-1.5 py-0.5 ${preset === 'tudo' ? 'border-[var(--color-accent)] text-[var(--color-text)]' : 'border-[var(--color-border)]'}`}
                title="Tudo contra tudo. Num modelo estrutural isto dá centenas de conflitos de construção (vigas a entrar nos pilares)."
              >
                Tudo × Tudo
              </button>
            </div>
            {preset === 'tudo' && (
              <p className="text-[var(--color-warn-text)]">
                Tudo × Tudo dá muito ruído: num modelo estrutural são
                centenas de conflitos de construção. Serve para uma primeira
                vista, não para triar.
              </p>
            )}
            <ClashSetsEditor
              label="Conjunto A"
              side={sideA}
              onChange={(s) => {
                setSide('a', s)
                setPreset('')
              }}
              inventories={inventories}
              count={countA}
              selectionCount={selectionCount}
              ruleBusy={clash.ruleBusy}
            />
            <ClashSetsEditor
              label="Conjunto B"
              side={sideB}
              onChange={(s) => {
                setSide('b', s)
                setPreset('')
              }}
              inventories={inventories}
              count={countB}
              selectionCount={selectionCount}
              ruleBusy={clash.ruleBusy}
            />
            {countA === 0 && countB === 0 && (
              <p className="text-[var(--color-text-dim)]">
                Começa por um preset, ou abre cada conjunto e escolhe as
                categorias (ou escreve uma regra). A análise só testa A
                contra B: é isso que separa trinta conflitos que interessam
                de centenas que não.
              </p>
            )}
            {clash.ruleMs > 500 && (
              <p className="text-[10px] text-[var(--color-text-dim)]">
                A regra levou {Math.round(clash.ruleMs)} ms a percorrer os
                modelos. Fica em cache: só volta a correr se a regra mudar.
              </p>
            )}
            {clash.sameModelWarning && (
              <p className="flex items-start gap-1 text-[var(--color-warn-text)]">
                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                <span>
                  Os dois conjuntos estão no mesmo modelo, e só nele, e o teste
                  dentro do mesmo modelo está desligado: a análise não devolve
                  nada.{' '}
                  <button
                    type="button"
                    onClick={() => setSelfClash(true)}
                    className="underline"
                  >
                    Ligar
                  </button>
                  .
                </span>
              </p>
            )}
          </div>

          <div className="shrink-0 border-b border-[var(--color-border)] px-3 py-1.5 text-[11px]">
            <button
              type="button"
              onClick={() => setShowConfig((v) => !v)}
              className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
              aria-expanded={showConfig}
            >
              {showConfig ? '▾' : '▸'} Configuração
            </button>
            {showConfig && (
              <div className="mt-1.5 flex flex-col gap-1.5">
                <label className="flex items-center gap-2">
                  <span className="w-32 shrink-0 text-[var(--color-text-dim)]">
                    Tolerância (mm)
                  </span>
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    value={mmValue(tolerance)}
                    onChange={(e) => setTolerance(parseMm(e.target.value))}
                    className="w-20 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1.5 py-0.5"
                  />
                  <span className="text-[10px] text-[var(--color-text-dim)]">
                    abaixo disto é toque, não interferência
                  </span>
                </label>
                <label className="flex items-center gap-2">
                  <span className="w-32 shrink-0 text-[var(--color-text-dim)]">
                    Penetração mínima (mm)
                  </span>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={mmValue(penetracaoMinima)}
                    onChange={(e) => setPenetracaoMinima(parseMm(e.target.value))}
                    className="w-20 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1.5 py-0.5"
                    aria-label="Penetração mínima para a interferência entrar na lista, em mm"
                  />
                  <span className="text-[10px] text-[var(--color-text-dim)]">
                    interferências mais rasas não entram na lista
                  </span>
                </label>
                <label className="flex items-center gap-2">
                  <span className="w-32 shrink-0 text-[var(--color-text-dim)]">
                    Folga (mm)
                  </span>
                  <input
                    type="number"
                    min={0}
                    step={5}
                    value={mmValue(clearance)}
                    onChange={(e) => setClearance(parseMm(e.target.value))}
                    className="w-20 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1.5 py-0.5"
                  />
                  <span className="text-[10px] text-[var(--color-text-dim)]">
                    0 desliga; elementos mais próximos do que isto
                  </span>
                </label>
                {clearance > 0 && (
                  <label className="flex items-center gap-2">
                    <span className="w-32 shrink-0 text-[var(--color-text-dim)]">
                      Ignorar contactos (mm)
                    </span>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={mmValue(ignoreContacts)}
                      onChange={(e) =>
                        setIgnoreContacts(parseMm(e.target.value))
                      }
                      className="w-20 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1.5 py-0.5"
                    />
                    <span className="text-[10px] text-[var(--color-text-dim)]">
                      esconde folgas de elementos encostados
                    </span>
                  </label>
                )}
                {clearance > 0 && (
                  <div
                    className="flex flex-wrap items-center gap-x-2 gap-y-0.5"
                    role="group"
                    aria-label="Pares de especialidades em que se verifica a folga"
                    title="A folga só se verifica entre estes pares de especialidades (MEP: MAC, HID e EEL). Por omissão MEP×EST e MEP×MEP: o ARQ fica fora da folga. Um modelo sem especialidade conhecida entra sempre."
                  >
                    <span className="w-32 shrink-0 text-[var(--color-text-dim)]">Folga entre</span>
                    {PARES_DE_FOLGA.map((p) => {
                      const on = folgaPares.includes(p)
                      const ultimo = on && folgaPares.length === 1
                      return (
                        <label
                          key={p}
                          className={`flex items-center gap-1 ${ultimo ? 'opacity-60' : ''}`}
                          title={ultimo ? 'Para não verificar folgas, ponha a folga a 0.' : undefined}
                        >
                          <input
                            type="checkbox"
                            checked={on}
                            disabled={ultimo}
                            onChange={(e) =>
                              setFolgaPares(e.target.checked ? [...folgaPares, p] : folgaPares.filter((x) => x !== p))
                            }
                            className="accent-[var(--color-accent)]"
                          />
                          {p}
                        </label>
                      )
                    })}
                  </div>
                )}
                <label
                  className="flex cursor-pointer items-center gap-2"
                  title="Sem isto, dois elementos do mesmo ficheiro IFC nunca são testados um contra o outro."
                >
                  <input
                    type="checkbox"
                    checked={selfClash}
                    onChange={(e) => setSelfClash(e.target.checked)}
                    className="accent-[var(--color-accent)]"
                  />
                  Testar elementos do mesmo modelo entre si
                </label>
                <p className="text-[10px] leading-snug text-[var(--color-text-dim)]">
                  A profundidade é uma estimativa por sondagem: serve para
                  ordenar por gravidade, não para medir. A folga é exata.
                </p>
              </div>
            )}
          </div>

          <div className="flex shrink-0 flex-col gap-1 border-b border-[var(--color-border)] px-3 py-2 text-[11px]">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => void detetar()}
                disabled={!clash.canRun}
                className="rounded bg-[var(--color-accent)] px-3 py-1 font-medium text-[var(--color-bg)] disabled:opacity-50"
              >
                {running ? 'A detetar…' : 'Detetar conflitos'}
              </button>
              {running && (
                <button
                  type="button"
                  onClick={clash.cancel}
                  className="rounded border border-[var(--color-border)] px-2 py-1 hover:text-[var(--color-text)]"
                >
                  Cancelar
                </button>
              )}
              {!running && ranAt > 0 && stats && (
                <span className="ml-auto font-mono text-[10px] text-[var(--color-text-dim)]">
                  {Math.round(stats.ms.total)} ms
                </span>
              )}
            </div>
            {running && (
              <>
                <div
                  className="h-1 w-full overflow-hidden rounded bg-[var(--color-border)]"
                  role="progressbar"
                  aria-valuenow={pct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div
                    className="h-full bg-[var(--color-accent)] transition-[width]"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-[10px] text-[var(--color-text-dim)]">
                  {phaseLabel} · {progress?.done ?? 0}/{progress?.total ?? 0} ·{' '}
                  {progress?.results ?? 0} encontrados
                </span>
              </>
            )}
            {!running && funilAntes && (
              <p className="text-[10px] leading-snug text-[var(--color-text-dim)]" data-testid="clash-funil-entrada">
                {funilAntes}
              </p>
            )}
            {error && <p className="text-[var(--color-error-text)]">{error}</p>}
            {!running && stats && (
              <p className="text-[10px] leading-snug text-[var(--color-text-dim)]">
                {stats.elements} elementos · {stats.candidatePairs} pares
                candidatos · {stats.testedPairs} testados · {stats.hard} duros ·{' '}
                {stats.clearance} folgas
                {stats.cancelled ? ' · CANCELADA (resultados parciais)' : ''}
                {stats.skippedBoxes > 0
                  ? ` · ${stats.skippedBoxes} sem caixa utilizável`
                  : ''}
                {stats.skippedHeavy > 0
                  ? ` · ${stats.skippedHeavy} saltados por serem pesados`
                  : ''}
                {stats.truncatedPairs
                  ? ' · teto de pares atingido (lista incompleta)'
                  : ''}
              </p>
            )}
            {!running && textoExcluidas && (
              <p className="text-[10px] leading-snug text-[var(--color-text-muted)]" data-testid="clash-excluidas-entrada">
                {textoExcluidas}
              </p>
            )}
          </div>
          <label
            className="flex shrink-0 items-center gap-2 border-b border-[var(--color-border)] px-3 py-1.5 text-[11px] text-[var(--color-text-muted)]"
            title="Depois de detetar, aplica as regras de classificação (prioridade, responsável, a quem comunicar) e junta os conflitos em grupos. Os pareceres manuais e de proposta ficam como estavam."
          >
            <input
              type="checkbox"
              checked={arrumarAoDetetar}
              onChange={(e) => setArrumarAoDetetar(e.target.checked)}
              className="accent-[var(--color-accent)]"
            />
            Classificar e agrupar ao detetar
          </label>
          {ranAt > 0 && !running && (
            <div
              className="flex shrink-0 flex-wrap items-center gap-2 px-3 py-2 text-[11px]"
              data-testid="clash-resumo-detetar"
            >
              <span className="flex-1 leading-snug text-[var(--color-text)]">
                {textoDoResumoDaCorrida(counts.total, prioridades)}
              </span>
              <button
                type="button"
                onClick={() => setSeparador('rever')}
                className="rounded bg-[var(--color-accent)] px-2 py-0.5 font-medium text-[var(--color-bg)]"
              >
                Ver a lista
              </button>
            </div>
          )}
          {ranAt > 0 && !running && !arrumando && arrumacao && (
            <p className="shrink-0 border-b border-[var(--color-border)] px-3 pb-2 text-[10px] leading-snug text-[var(--color-text-dim)]" data-testid="clash-arrumacao-feita">
              {arrumacao}
            </p>
          )}
            {resumoCorrida && (
              <div
                className="flex shrink-0 items-center gap-2 border-b border-[var(--color-border)] bg-[var(--color-accent-bg)] px-3 py-1.5 text-[11px]"
                role="status"
                data-testid="clash-resumo-corrida"
              >
                <span className="flex-1 leading-snug">{resumoCorrida}</span>
                <button
                  type="button"
                  onClick={() => setResumoCorrida(null)}
                  className="shrink-0 rounded border border-[var(--color-border)] px-1.5 py-0.5"
                  title="Fechar aviso"
                >
                  Fechar
                </button>
              </div>
            )}

            {missing.length > 0 && (
              <div className="flex shrink-0 items-center gap-2 border-b border-[var(--color-border)] px-3 py-1.5 text-[11px] text-[var(--color-text-muted)]">
                <span className="flex-1 leading-snug">
                  {missing.length} conflito{missing.length === 1 ? '' : 's'} com
                  parecer já não aparece
                  {missing.length === 1 ? '' : 'm'} nesta corrida. Fica
                  {missing.length === 1 ? '' : 'm'} na triagem como resolvido
                  {missing.length === 1 ? '' : 's'} ou por verificar, com a razão.
                </span>
                <button
                  type="button"
                  onClick={() => forgetTriage(missing.map((m) => m.key))}
                  className="shrink-0 rounded border border-[var(--color-border)] px-1.5 py-0.5 hover:text-[var(--color-text)]"
                  title="Apaga os pareceres desses conflitos"
                >
                  Esquecer
                </button>
              </div>
            )}

            <ClashClassificacaoPainel results={results} checked={checked} focusKey={focusKey} />

            <ClashExclusaoPainel
              results={results}
              avaliacao={avaliacao}
              especialidadeDe={especialidadeDe}
              aberto={exclusaoAberta}
              onAberto={setExclusaoAberta}
              onTopicoModelador={onTopicoModelador}
            />

          <div className="flex shrink-0 flex-wrap items-center gap-1 border-b border-[var(--color-border)] px-3 py-1.5 text-[11px]">
              <button
                type="button"
                onClick={() => propostaRef.current?.click()}
                className="rounded border border-[var(--color-border)] px-1.5 py-0.5"
                title="Preenche a triagem a partir de um ficheiro JSON com a prioridade, o responsável, a quem comunicar, a recomendação e as etiquetas, por conflito ou por grupo (formato em docs/CLASH.md)"
              >
                Importar proposta (JSON)
              </button>
              <input
                ref={propostaRef}
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  e.target.value = ''
                  if (f) void onImportProposal(f)
                }}
              />
          </div>
            {propostaMsg && (
              <div className="flex shrink-0 items-center gap-2 border-b border-[var(--color-border)] px-3 py-1.5 text-[11px] text-[var(--color-text-muted)]">
                <span className="flex-1 leading-snug">{propostaMsg}</span>
                <button
                  type="button"
                  onClick={() => setPropostaMsg(null)}
                  className="shrink-0 rounded border border-[var(--color-border)] px-1.5 py-0.5"
                  title="Fechar aviso"
                >
                  Fechar
                </button>
              </div>
            )}

        </div>
      )}

      {separador === 'rever' && ranAt > 0 && (
        <div className="flex min-h-0 flex-1 flex-col" role="tabpanel" aria-label="Rever">
            <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-[var(--color-border)] px-3 py-1.5 text-[11px]">
              <span className="text-[var(--color-text)]">
                {counts.total} conflito{counts.total === 1 ? '' : 's'}
                {ignorados.size > 0 ? ' na lista' : ''}
              </span>
              <span className="text-[var(--color-text-muted)]">
                {counts.novo} {TRIAGE_LABELS.novo.toLowerCase()}s
              </span>
              {counts.aceite > 0 && <span className="text-[var(--color-warn-text)]">{counts.aceite} aceites</span>}
              {counts.resolvido > 0 && (
                <span className="text-[var(--color-ok-text)]">
                  {counts.resolvido} resolvidos
                </span>
              )}
              {counts.reaberto > 0 && (
                <span className="text-[var(--color-error-text)]">
                  {counts.reaberto} reaberto{counts.reaberto === 1 ? '' : 's'}
                </span>
              )}
              <div className="ml-auto flex items-center gap-0.5" role="group" aria-label="Modo da lista">
                {(['grupos', 'conflitos'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setModo(m)}
                    disabled={m === 'grupos' && !grupos}
                    aria-pressed={modo === m}
                    className={`rounded border px-1.5 py-0.5 disabled:opacity-40 ${
                      modo === m ? 'border-[var(--color-accent)] text-[var(--color-text)]' : 'border-[var(--color-border)]'
                    }`}
                    title={m === 'grupos' && !grupos ? 'Sem grupos: classifique e agrupe primeiro' : undefined}
                  >
                    {m === 'grupos' ? 'Por grupos' : 'Por conflito'}
                  </button>
                ))}
              </div>
              {modo === 'conflitos' && (
              <label className="flex items-center gap-1 text-[var(--color-text-dim)]">
                Agrupar
                <select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value as ClashGroupBy)}
                  className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 py-0.5"
                >
                  <option value="severity">gravidade</option>
                  <option value="categories">categorias</option>
                  <option value="models">modelos</option>
                  <option value="grupos">grupos de tópicos</option>
                </select>
              </label>
              )}
            </div>

            <div
              className="flex shrink-0 flex-wrap items-center gap-x-2 gap-y-1 border-b border-[var(--color-border)] px-3 py-1 text-[11px]"
              data-testid="clash-filtros-rapidos"
            >
              <label
                className="flex items-center gap-1"
                title={topo ? textoDoTopo(topo) : undefined}
                data-testid="clash-topo-grupos"
              >
                <input
                  type="checkbox"
                  checked={filtros.soReuniao}
                  onChange={(e) => setFiltros({ ...filtros, soReuniao: e.target.checked })}
                  className="accent-[var(--color-accent)]"
                />
                Só para reunião
              </label>
              <select
                value={filtros.especialidade}
                onChange={(e) => setFiltros({ ...filtros, especialidade: e.target.value })}
                aria-label="Filtrar por especialidade responsável"
                className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 py-0.5"
              >
                <option value="">todas as especialidades</option>
                {opcoesFiltros.especialidades.map((e) => (
                  <option key={e} value={e}>
                    {e}
                  </option>
                ))}
              </select>
              <select
                value={filtros.nivel}
                onChange={(e) => setFiltros({ ...filtros, nivel: e.target.value })}
                aria-label="Filtrar por nível"
                className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 py-0.5"
              >
                <option value="">todos os níveis</option>
                {opcoesFiltros.niveis.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              {ignorados.size > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setExclusaoAberta(true)
                    setSeparador('detetar')
                  }}
                  className="rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                  title="Os conflitos apanhados pelas regras de exclusão ligadas (no passo Detetar): rever, reabrir um a um ou a regra inteira"
                >
                  Ignorados por regra ({ignorados.size})
                </button>
              )}
              {temFiltros(filtros) && (
                <>
                  <span className="text-[var(--color-text-dim)]">
                    {visiveis.length} de {trabalho.length}
                  </span>
                  <button
                    type="button"
                    onClick={() => setFiltros(SEM_FILTROS)}
                    className="rounded border border-[var(--color-border)] px-1.5 py-0.5"
                  >
                    Limpar filtros
                  </button>
                </>
              )}
            </div>

            <div className="flex shrink-0 flex-wrap items-center gap-1 border-b border-[var(--color-border)] px-3 py-1.5 text-[11px]">
              <ControlosDoRealce contexto="conflito" />
              <button
                type="button"
                onClick={() => void onIsolate()}
                disabled={!focused}
                className="rounded border border-[var(--color-border)] px-1.5 py-0.5 disabled:opacity-40"
                title="Esconde tudo menos os dois elementos do conflito"
              >
                Isolar
              </button>
              <button
                type="button"
                onClick={() => void onShowAll()}
                disabled={!isolated}
                className="rounded border border-[var(--color-border)] px-1.5 py-0.5 disabled:opacity-40"
              >
                Mostrar tudo
              </button>
              <button
                type="button"
                onClick={() => void restorePreviousView()}
                disabled={!hasPreviousView()}
                className="rounded border border-[var(--color-border)] px-1.5 py-0.5 disabled:opacity-40"
                title="Volta a câmara ao sítio onde estava antes do primeiro conflito"
              >
                Vista anterior
              </button>
            </div>

            {checked.size > 0 && (
              <div className="flex shrink-0 flex-wrap items-center gap-1 border-b border-[var(--color-border)] px-3 py-1.5 text-[11px]">
                <span className="text-[var(--color-text-muted)]">
                  {checked.size} marcado{checked.size === 1 ? '' : 's'}:
                </span>
                <select
                  value={lotePrioridade}
                  onChange={(e) => setLotePrioridade(e.target.value as ClashPriority | '')}
                  aria-label="Prioridade a aplicar"
                  className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 py-0.5"
                >
                  <option value="">prioridade</option>
                  {PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {PRIORITY_LABELS[p]}
                    </option>
                  ))}
                </select>
                <input
                  list="clash-lote-especialidades"
                  value={loteResponsavel}
                  onChange={(e) => setLoteResponsavel(e.target.value)}
                  placeholder="responsável"
                  aria-label="Especialidade responsável a aplicar"
                  className="w-24 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 py-0.5"
                />
                <datalist id="clash-lote-especialidades">
                  {sugestoesDeEspecialidade(modelNames).map((s) => (
                    <option key={s} value={s} />
                  ))}
                </datalist>
                <input
                  type="date"
                  value={lotePrazo}
                  onChange={(e) => setLotePrazo(e.target.value)}
                  aria-label="Prazo a aplicar"
                  className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 py-0.5"
                />
                <button
                  type="button"
                  onClick={onApplyBatch}
                  disabled={!lotePrioridade && !loteResponsavel.trim() && !lotePrazo}
                  className="rounded border border-[var(--color-border)] px-1.5 py-0.5 disabled:opacity-40"
                >
                  Aplicar aos marcados
                </button>
                <button
                  type="button"
                  onClick={() => setChecked([])}
                  className="ml-auto rounded border border-[var(--color-border)] px-1.5 py-0.5"
                >
                  Desmarcar
                </button>
              </div>
            )}

            {focused && (
              <details className="shrink-0 border-b border-[var(--color-border)] px-3 py-1.5 text-[11px]">
                <summary
                  className="cursor-pointer text-[var(--color-text-muted)]"
                  title={`${nomeDe(focused.pair.modelA)?.completo ?? focused.pair.modelA} x ${nomeDe(focused.pair.modelB)?.completo ?? focused.pair.modelB}`}
                >
                  Conflito em foco: {clashLabel(focused)} (
                  {nomeDe(focused.pair.modelA)?.minimo ?? focused.pair.modelA} x{' '}
                  {nomeDe(focused.pair.modelB)?.minimo ?? focused.pair.modelB})
                </summary>
                <div className="mt-1">
                  <ClashPartilhaEdicao identidade={motorDaPartilha()?.identidade(focusKey) ?? null} />
                  <ClashTriageEditor
                    entry={triageOf(triage, clashKey(focused))}
                    modelos={modelNames}
                    onPatch={(patch) => onPatch(focused, patch)}
                  />
                </div>
              </details>
            )}

            {modo === 'grupos' ? (
              <ClashGruposLista
                estadoNoBcf={estadoDoGrupoNoBcf}
                results={visiveis}
                grupos={classificacao.grupos}
                onLote={onLoteDoGrupo}
                triage={triage}
                checked={checked}
                focusKey={focusKey}
                onOpen={onOpenClash}
                onTriage={onTriage}
                onPatch={onPatch}
                onToggleChecked={toggleChecked}
                onSetChecked={onSetChecked}
                modelos={modelNames}
              />
            ) : (
              <ClashResultsList
                results={visiveis}
                groupBy={groupBy}
                triage={triage}
                checked={checked}
                focusKey={focusKey}
                onOpen={onOpenClash}
                onTriage={onTriage}
                onPatch={onPatch}
                onToggleChecked={toggleChecked}
                onSetChecked={onSetChecked}
                modelos={modelNames}
              />
            )}
          <div className="flex shrink-0 items-center justify-end border-t border-[var(--color-border)] px-3 py-1.5 text-[11px]">
            <button
              type="button"
              onClick={() => setSeparador('entregar')}
              className="rounded border border-[var(--color-border)] px-2 py-0.5 hover:text-[var(--color-text)]"
            >
              Seguinte: entregar BCF
            </button>
          </div>
        </div>
      )}

      {separador === 'entregar' && ranAt > 0 && (
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto" role="tabpanel" aria-label="Entregar BCF">
          {bcfDoEnvio && (
            <div
              className="flex shrink-0 flex-wrap items-center gap-2 border-b border-[var(--color-border)] px-3 py-1.5 text-[11px]"
              data-testid="clash-bcf-do-envio"
            >
              <span className="flex-1 leading-snug">
                BCF «{bcfDoEnvio.name}» no módulo:{' '}
                {formatarInteiro(latestParsed(bcfDoEnvio).topics.length)} tópicos.
              </span>
              {workspace && (
                <AbrirNoModuloBcf slug={workspace.slug} localId={bcfDoEnvio.localId} />
              )}
            </div>
          )}
            {propostaModulo && envioModulo && (
              <div
                className="flex shrink-0 flex-col gap-1 border-b border-[var(--color-border)] px-3 py-1.5 text-[11px]"
                data-testid="clash-alteracoes-modulo"
              >
                <span className="leading-snug">
                  A última corrida altera {propostaModulo.alteracoes.length} tópico
                  {propostaModulo.alteracoes.length === 1 ? '' : 's'} do BCF
                  {bcfDoEnvio ? ` "${bcfDoEnvio.name}"` : ''}. Nada é alterado sem confirmação.
                </span>
                <ul className="flex max-h-32 flex-col gap-0.5 overflow-auto text-[10px] text-[var(--color-text-muted)]">
                  {propostaModulo.alteracoes.map((a) => (
                    <li key={a.guid}>
                      <span className="text-[var(--color-text)]">{a.titulo}</span>
                      {a.estadoDe !== a.estadoPara
                        ? `: estado ${a.estadoDe ?? 'sem estado'} para ${a.estadoPara}`
                        : ''}
                      {`; comentário: ${a.comentario.split('\n').join(' ')}`}
                    </li>
                  ))}
                </ul>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={onAplicarAoModulo}
                    className="rounded border border-[var(--color-accent)] px-1.5 py-0.5"
                  >
                    Aplicar ao BCF
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setEnvioModulo({ ...envioModulo, aplicadoAte: propostaModulo.ate })
                    }
                    className="rounded border border-[var(--color-border)] px-1.5 py-0.5"
                    title="Não altera o BCF; estas alterações deixam de ser propostas"
                  >
                    Ignorar
                  </button>
                </div>
              </div>
            )}

            {novosParaModulo.length > 0 && envioModulo && bcfDoEnvio && (
              <div
                className="flex shrink-0 flex-col gap-1 border-b border-[var(--color-border)] px-3 py-1.5 text-[11px]"
                data-testid="clash-novos-modulo"
              >
                <span className="leading-snug">
                  {novosParaModulo.length} conflito{novosParaModulo.length === 1 ? '' : 's'} novo
                  {novosParaModulo.length === 1 ? '' : 's'} sem tópico no BCF "{bcfDoEnvio.name}"
                  {checked.size > 0 ? ' (entre os marcados)' : soComTriagem ? ' (com triagem)' : ' (por resolver)'}.
                  Nada é criado sem confirmação.
                </span>
                <ul className="flex max-h-32 flex-col gap-0.5 overflow-auto text-[10px] text-[var(--color-text-muted)]">
                  {novosParaModulo.map((r) => {
                    const g = triage[clashKey(r)]?.group
                    return (
                      <li key={clashKey(r)}>
                        <span className="text-[var(--color-text)]">{clashLabel(r)}</span>
                        {g ? ` (grupo ${g})` : ''}
                      </li>
                    )
                  })}
                </ul>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => void onAcrescentarAoModulo()}
                    disabled={acrescentando || exporting}
                    className="rounded border border-[var(--color-accent)] px-1.5 py-0.5 disabled:opacity-40"
                    title="Cria um tópico por conflito novo, como na exportação, e liga-o aos tópicos do mesmo grupo que já estão no BCF"
                  >
                    {acrescentando ? 'A acrescentar…' : 'Acrescentar os novos ao BCF'}
                  </button>
                </div>
              </div>
            )}

          <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-[var(--color-border)] px-3 py-2 text-[11px]">
              <label
                className="flex items-center gap-1 text-[var(--color-text-dim)]"
                title="Uma imagem JPEG por tópico (1280 x 720), com a câmara do viewpoint, o realce A/B e a caixa de corte. A vista do utilizador é reposta no fim."
              >
                <input
                  type="checkbox"
                  checked={incluirImagens}
                  onChange={(e) => setIncluirImagens(e.target.checked)}
                  className="accent-[var(--color-accent)]"
                />
                Incluir imagens
              </label>
              {grupos && topicosPorOpcao && (
                <fieldset
                  className="flex basis-full flex-wrap items-center gap-x-3 gap-y-1 rounded border border-[var(--color-border)] px-2 py-1"
                  title="Um tópico por conflito: cada conflito sai com o seu viewpoint e a sua imagem, e o grupo vai indicado na etiqueta Grupo, nos tópicos relacionados (RelatedTopics) e na primeira linha da descrição; o módulo BCF agrupa a lista por eles. Um tópico por grupo: os conflitos do grupo saem num só tópico, com todos os elementos do grupo no viewpoint."
                >
                  <legend className="px-1 text-[var(--color-text-dim)]">Tópicos BCF dos grupos</legend>
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      name="topicos-dos-grupos"
                      value="conflito"
                      checked={topicosDosGrupos === 'conflito'}
                      onChange={() => setTopicosDosGrupos('conflito')}
                      className="accent-[var(--color-accent)]"
                    />
                    Um tópico por conflito, com o grupo indicado ({formatarInteiro(topicosPorOpcao.conflito)}{' '}
                    {topicosPorOpcao.conflito === 1 ? 'tópico' : 'tópicos'})
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      name="topicos-dos-grupos"
                      value="grupo"
                      checked={topicosDosGrupos === 'grupo'}
                      onChange={() => setTopicosDosGrupos('grupo')}
                      className="accent-[var(--color-accent)]"
                    />
                    Um tópico por grupo ({formatarInteiro(topicosPorOpcao.grupo)}{' '}
                    {topicosPorOpcao.grupo === 1 ? 'tópico' : 'tópicos'})
                  </label>
                </fieldset>
              )}
              <label
                className="flex items-center gap-1 text-[var(--color-text-dim)]"
                title="Exporta só os conflitos com triagem (estado decidido, prioridade, responsável ou grupo da proposta). Serve para correr com folga sem levar para o BCF as folgas que não estão na proposta."
              >
                <input
                  type="checkbox"
                  checked={soComTriagem}
                  onChange={(e) => setSoComTriagem(e.target.checked)}
                  className="accent-[var(--color-accent)]"
                />
                Só conflitos com triagem
              </label>
              <label
                className="flex items-center gap-1 text-[var(--color-text-dim)]"
                title="Exporta também os conflitos triados que deixaram de ocorrer (resolvidos), com a última geometria conhecida. Os tópicos saem com os mesmos Guids e o estado Closed: quem importar o BCF de novo vê-os fechados."
              >
                <input
                  type="checkbox"
                  checked={incluirResolvidos}
                  onChange={(e) => setIncluirResolvidos(e.target.checked)}
                  className="accent-[var(--color-accent)]"
                />
                Incluir os resolvidos
              </label>
              {ignorados.size > 0 && (
                <label
                  className="flex items-center gap-1 text-[var(--color-text-dim)]"
                  title="Por omissão, os conflitos ignorados por regra de exclusão não vão para o BCF. Ligar leva-os também."
                >
                  <input
                    type="checkbox"
                    checked={incluirIgnorados}
                    onChange={(e) => setIncluirIgnorados(e.target.checked)}
                    className="accent-[var(--color-accent)]"
                  />
                  Incluir os ignorados por regra ({ignorados.size})
                </label>
              )}
              {!bcfModuleEnabled && (
                <label
                  className={`flex items-center gap-1 text-[var(--color-text-dim)] ${ultimaExportacao ? '' : 'opacity-50'}`}
                  title={
                    ultimaExportacao
                      ? `Exporta só os conflitos novos (nunca exportados) e os que mudaram de estado desde a exportação de ${dataCurtaPt(ultimaExportacao.data)}, com os mesmos Guids: uma plataforma que junte por Guid actualiza os tópicos que já tem.`
                      : 'Disponível depois da primeira exportação para ficheiro: é ela que fica registada para comparar.'
                  }
                >
                  <input
                    type="checkbox"
                    checked={soAlteracoes}
                    disabled={!ultimaExportacao}
                    onChange={(e) => setSoAlteracoes(e.target.checked)}
                    className="accent-[var(--color-accent)]"
                  />
                  Só o que mudou desde a última exportação
                </label>
              )}
              {!bcfModuleEnabled && (
                <select
                  value={versaoBcf}
                  onChange={(e) => setVersaoBcf(e.target.value as VersaoBcf)}
                  title="Versão do BCF. A 2.1 é a que todas as plataformas leem."
                  className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 py-0.5"
                >
                  <option value="2.1">BCF 2.1</option>
                  <option value="3.0">BCF 3.0</option>
                </select>
              )}
              <button
                type="button"
                onClick={() => void onExport()}
                disabled={exporting || results.length === 0}
                className="ml-auto rounded bg-[var(--color-accent)] px-3 py-1 font-medium text-[var(--color-bg)] disabled:opacity-40"
                title={
                  checked.size > 0
                    ? `Exporta os ${checked.size} conflitos marcados`
                    : 'Exporta os conflitos por resolver'
                }
              >
                {exporting
                  ? 'A exportar…'
                  : bcfModuleEnabled
                    ? 'Criar BCF'
                    : 'Exportar .bcfzip'}
              </button>
          </div>
            {delta && ultimaExportacao && (
              <div
                className="flex shrink-0 items-center gap-2 border-b border-[var(--color-border)] px-3 py-1 text-[10px] text-[var(--color-text-muted)]"
                data-testid="clash-delta-exportacao"
              >
                Desde a exportação de {dataCurtaPt(ultimaExportacao.data)}: {delta.novos.length} novo
                {delta.novos.length === 1 ? '' : 's'} e {delta.alterados.length} alterado
                {delta.alterados.length === 1 ? '' : 's'}
                {delta.aExportar.length > delta.novos.length + delta.alterados.length
                  ? ` (${delta.aExportar.length} no ficheiro, com os outros conflitos dos grupos tocados)`
                  : ''}
                .
              </div>
            )}

          <details className="shrink-0 border-b border-[var(--color-border)] text-[11px]">
            <summary className="cursor-pointer px-3 py-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
              Avançado
            </summary>
            <div className="flex shrink-0 flex-wrap items-center gap-1 border-b border-[var(--color-border)] px-3 py-1 text-[10px] text-[var(--color-text-dim)]">
              <span>Coordenadas BCF no referencial de: {rotuloReferencial}</span>
              <select
                value={referencialModelo}
                onChange={(e) => setReferencialModelo(e.target.value)}
                aria-label="Modelo de referência das coordenadas BCF"
                className="ml-auto rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 py-0.5"
              >
                <option value="">automático</option>
                {models.map((m) => (
                  <option key={m.id} value={m.id} title={nomeDe(m.id)?.completo ?? m.fileName}>
                    {nomeDe(m.id)?.curto ?? m.fileName}
                  </option>
                ))}
              </select>
            </div>

          </details>
        </div>
      )}
    </div>
  )
}

/** "Abrir no módulo BCF": à parte porque o painel também se monta fora
 *  do router (testes, modo local), e o useNavigate só existe dentro. */
function AbrirNoModuloBcf({ slug, localId }: { slug: string; localId: string }) {
  const navigate = useNavigate()
  return (
    <button
      type="button"
      onClick={() => navigate(`/${slug}/bcf/${encodeURIComponent(localId)}`)}
      className="rounded border border-[var(--color-border)] px-1.5 py-0.5 hover:text-[var(--color-text)]"
    >
      Abrir no módulo BCF
    </button>
  )
}
